













































import { computed, defineComponent, ref } from "@vue/composition-api";
import { cloneDeep } from "lodash-es";
import { PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import { useAiUserSettings } from "@/hooks/useAiUserSettings";
import useAuthUser from "@/hooks/useAuthUser";
import usePaymentPackages from "@/hooks/usePaymentPackages";
import { useStaticPages } from "@/hooks/useStaticPages";
import useSystemSettings from "@/hooks/useSystemSettings";
import config from "@/config";
import BaseIcon from "@/components/Base/BaseIcon";
import BaseButton from "@/components/BaseButton";
import BasePage from "@/components/BasePage";
import PageTitle from "@/components/PageTitle";
import DarkModeSwitch from "./components/DarkModeSwitch";
import MoreMenuItem from "./components/MoreMenuItem/MoreMenuItem.vue";
import { IMoreMenuItem, MenuGroups, moreMenu } from "./config";

const DEFAULT_SECTION = "default";

export default defineComponent({
  name: "PageMore",
  components: {
    MoreMenuItem,
    BasePage,
    PageTitle,
    BaseButton,
    BaseIcon,
    DarkModeSwitch,
  },
  props: {
    section: { type: String, default: DEFAULT_SECTION },
  },
  setup(props) {
    const { creditCardAllowed } = useSystemSettings();
    const { authUser, authorities } = useAuthUser();
    const { pages, getPages } = useStaticPages();
    const { aiAllowed, getAiUserSettings } = useAiUserSettings();
    const { paymentPackages: packageList, getPaymentPackages } = usePaymentPackages([PaymentPackageEnum.SUBSCRIPTION]);
    const loading = ref(false);

    const isDefaultMenuSection = computed(() => props.section === DEFAULT_SECTION);
    const showDarkModeSwitch = computed(() => config.featureDarkMode && isDefaultMenuSection.value);
    const showPoweredBy = computed(() => !config.featureHideScrileLink);
    const checkedCallAuthorities = computed(() => checkAuthorities(authorities.value, ["updateMyCallPrice"]));
    const checkedMessageAuthorities = computed(() => checkAuthorities(authorities.value, ["updateMyMessagePrice"]));
    const checkedCallOptionsAuthorities = computed(() => checkAuthorities(authorities.value, ["updateMyCallOptions"]));

    const aboutLinks = computed<IMoreMenuItem[]>(() => {
      return pages.value.reduce((out, page, index) => {
        if (page.visible) out.push({ label: page.title, href: page.path, name: `term${index}` });
        return out;
      }, [] as IMoreMenuItem[]);
    });

    const showPriceSettings = computed(
      () =>
        (config.featureCalling && checkedCallAuthorities.value) ||
        (config.featurePaidSentMessage && checkedMessageAuthorities.value) ||
        checkedCallOptionsAuthorities.value
    );

    const menu = computed(() => {
      const menu = cloneDeep(moreMenu);
      menu.terms.list = aboutLinks.value;
      return menu;
    });

    const title = computed(() => menu.value[props.section].title);
    const hideBack = computed(() => menu.value[props.section].hideBack);

    const menuList = computed<IMoreMenuItem[]>(() => {
      return menu.value[props.section].list.filter(item => {
        if (item.name === "priceSettings" && !showPriceSettings.value) {
          return false;
        }
        if (item.name === "subscriptionManagement" && packageList.value.length === 0) {
          return false;
        }
        if (item.name === "aiQuestionary" && !aiAllowed.value) {
          return false;
        }
        if (item.filter?.requiredRole && item.filter.requiredRole.every(i => i !== authUser.value?.role)) {
          return false;
        }
        if (item.filter?.forbiddenRole && item.filter?.forbiddenRole === authUser.value?.role) {
          return false;
        }
        if (item.filter?.requiredAuthorities && !checkAuthorities(authorities.value, item.filter.requiredAuthorities)) {
          return false;
        }
        if (item.filter?.requiredCreditCard && !creditCardAllowed.value) {
          return false;
        }
        return true;
      });
    });

    const menuGrouped = computed<Partial<Record<MenuGroups, IMoreMenuItem[]>>>(() => {
      const menu: Partial<Record<MenuGroups, IMoreMenuItem[]>> = {};
      if (!isDefaultMenuSection.value) {
        menu.OTHER = menuList.value;
      } else {
        const keys = Object.keys(MenuGroups) as Array<keyof typeof MenuGroups>;
        keys.forEach(key => {
          const list = menuList.value.filter(item => item.group === MenuGroups[key]);
          if (list.length > 0) {
            menu[key] = list;
          }
        });
      }

      return menu;
    });

    const getData = async () => {
      loading.value = true;
      const promises: Promise<any>[] = [getPages(), getPaymentPackages()];
      if (config.featureAIMessages) {
        promises.push(getAiUserSettings());
      }
      try {
        await Promise.all(promises);
      } finally {
        loading.value = false;
      }
    };

    getData();

    return {
      isDefaultMenuSection,
      creditCardAllowed,
      authUser,
      authorities,
      loading,
      showPoweredBy,
      showDarkModeSwitch,
      aboutLinks,
      title,
      hideBack,
      menuList,
      menuGrouped,
    };
  },
});
